/**
 * Cryptographically secure random generation for security-relevant values
 * (share codes, access tokens, invite codes).
 *
 * Every helper draws from the platform CSPRNG via `crypto.getRandomValues`.
 * There is deliberately NO `Math.random` fallback: `Math.random` is not
 * cryptographically secure and is predictable, so a "fallback" would silently
 * downgrade a security credential. If a secure RNG is unavailable we throw so
 * the caller fails loudly instead of issuing a guessable code.
 */

/**
 * Returns the platform CSPRNG, or throws if none is available.
 * @returns {Crypto}
 */
function getCrypto() {
  const c =
    (typeof globalThis !== 'undefined' && globalThis.crypto) ||
    (typeof window !== 'undefined' && window.crypto) ||
    null;
  if (!c || typeof c.getRandomValues !== 'function') {
    throw new Error(
      'Secure random generation is unavailable: crypto.getRandomValues is not supported in this environment.'
    );
  }
  return c;
}

/**
 * Fills and returns a Uint8Array of `length` cryptographically secure bytes.
 * @param {number} length
 * @returns {Uint8Array}
 */
export function secureRandomBytes(length) {
  if (!Number.isInteger(length) || length <= 0) {
    throw new Error('secureRandomBytes: length must be a positive integer.');
  }
  const array = new Uint8Array(length);
  getCrypto().getRandomValues(array);
  return array;
}

/**
 * Generates a string of `count` decimal digits (0-9) using rejection sampling
 * so every digit is uniformly distributed (no modulo bias).
 * @param {number} count
 * @returns {string}
 */
export function secureDigits(count) {
  if (!Number.isInteger(count) || count <= 0) {
    throw new Error('secureDigits: count must be a positive integer.');
  }
  const c = getCrypto();
  let out = '';
  const buf = new Uint8Array(1);
  while (out.length < count) {
    c.getRandomValues(buf);
    // Reject the top 6 values (250-255) so 0-249 maps evenly across 0-9.
    if (buf[0] < 250) {
      out += (buf[0] % 10).toString();
    }
  }
  return out;
}

/**
 * Generates a lowercase hex token from `byteLength` secure random bytes
 * (output length is byteLength * 2 hex chars).
 * @param {number} byteLength
 * @returns {string}
 */
export function secureHexToken(byteLength) {
  return Array.from(secureRandomBytes(byteLength), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('');
}

/**
 * Generates a `length`-character code drawn uniformly from `alphabet` using
 * rejection sampling to avoid modulo bias.
 * @param {string} alphabet - non-empty set of allowed characters
 * @param {number} length
 * @returns {string}
 */
export function secureCodeFromAlphabet(alphabet, length) {
  if (typeof alphabet !== 'string' || alphabet.length === 0) {
    throw new Error('secureCodeFromAlphabet: alphabet must be a non-empty string.');
  }
  if (!Number.isInteger(length) || length <= 0) {
    throw new Error('secureCodeFromAlphabet: length must be a positive integer.');
  }
  const n = alphabet.length;
  // Largest multiple of n that fits in a byte; bytes >= this are rejected to
  // keep the distribution uniform.
  const limit = Math.floor(256 / n) * n;
  const c = getCrypto();
  const buf = new Uint8Array(1);
  let out = '';
  while (out.length < length) {
    c.getRandomValues(buf);
    if (buf[0] < limit) {
      out += alphabet.charAt(buf[0] % n);
    }
  }
  return out;
}
